import {observer} from "mobx-react";
import {useCallback, useContext, useMemo, useRef} from "react";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  IconButton,
  LinearProgress,
  Typography,
} from "@mui/material";
import {useTheme} from "@mui/material/styles";
import CloseIcon from "@mui/icons-material/Close";
import VideoFileIcon from "@mui/icons-material/VideoFile";
import DeleteIcon from "@mui/icons-material/Delete";
import {useDropzone} from "react-dropzone";
import {useNavigate} from "react-router";
import {makeAutoObservable} from "mobx";
import {Frame} from "../common/Frame";
import {SessionMetaForm, SessionMetaFormData} from "./SessionMetaForm";
import {UploadStore} from "../../stores/UploadStore";
import {AppContext} from "../../stores/AppStore";
import {D} from "../../Constants";
import {proto} from "../../proto/messages";
import {recordings} from "../../proto/compiled";
import {ErrorUtils} from "../../utils/ErrorUtils";
import {AppRoutes} from "../../AppRoutes";
import {RecordingsStore} from "../../stores/RecordingsStore";

const VIDEO_EXTENSIONS = new Set(['mp4', 'mov', 'avi', 'mkv', 'webm', 'm4v']);

class UploadSessionStore {
  uploadStore: UploadStore;
  formData: SessionMetaFormData = {
    title: '',
    description: '',
    participants: [],
  };
  videoDurationMs = 0;
  videoPreviewUrl?: string;
  creating = false;

  constructor(uploadStore: UploadStore) {
    this.uploadStore = uploadStore;
    makeAutoObservable(this);
  }

  setFormData(data: SessionMetaFormData) {
    this.formData = data;
  }

  setVideoDuration(ms: number) {
    this.videoDurationMs = ms;
  }

  setVideoPreviewUrl(url?: string) {
    this.videoPreviewUrl = url;
  }

  get isUploading() {
    return this.uploadStore.started && !this.uploadStore.done;
  }

  get isUploaded() {
    return this.uploadStore.done && !!this.uploadStore.uploadedFile?.id;
  }

  get canCreate() {
    return this.isUploaded && this.formData.title.trim().length > 0;
  }

  reset() {
    this.uploadStore.cancel();
    this.formData = {title: '', description: '', participants: []};
    this.videoDurationMs = 0;
    if (this.videoPreviewUrl) {
      URL.revokeObjectURL(this.videoPreviewUrl);
      this.videoPreviewUrl = undefined;
    }
  }

  dispose() {
    this.reset();
  }
}

interface UploadSessionDialogProps {
  open: boolean;
  onClose: () => void;
}

export const UploadSessionDialog = observer(({open, onClose}: UploadSessionDialogProps) => {
  const app = useContext(AppContext)!;
  const theme = useTheme();
  const navigate = useNavigate();
  const videoRef = useRef<HTMLVideoElement>(null);

  const store = useMemo(() => {
    const uploadStore = new UploadStore(app, VIDEO_EXTENSIONS);
    return new UploadSessionStore(uploadStore);
  }, [app]);
  const uploadStore = store.uploadStore;

  const onDrop = useCallback((acceptedFiles: File[]) => {
    // Create preview URL for the video
    if (acceptedFiles.length > 0) {
      const file = acceptedFiles[0];
      const previewUrl = URL.createObjectURL(file);
      store.setVideoPreviewUrl(previewUrl);

      // Auto-fill title from filename (without extension)
      if (!store.formData.title) {
        const nameWithoutExt = file.name.replace(/\.[^/.]+$/, '');
        store.setFormData({...store.formData, title: nameWithoutExt});
      }
    }

    uploadStore.uploadFile(acceptedFiles);
  }, [store, uploadStore]);

  const handleVideoLoad = useCallback(() => {
    if (!videoRef.current) return;
    const durationMs = Math.round(videoRef.current.duration * 1000);
    store.setVideoDuration(durationMs);
  }, [store]);

  const handleCreate = async () => {
    if (!uploadStore.uploadedFile?.id) return;

    store.creating = true;
    try {
      const response = await app.socketApi.fetch(
        proto.TxCreateRecording.create({
          videoFileId: uploadStore.uploadedFile.id,
          title: store.formData.title,
          description: store.formData.description,
          participants: store.formData.participants.map(p =>
            recordings.RecordingParticipant.create({
              id: p.id?.startsWith('temp-') ? '' : p.id,
              name: p.name,
              role: p.role,
            })
          ),
          durationMs: store.videoDurationMs,
        }),
        new proto.RxCreateRecordingResponse(),
      );

      if (response.proto.recordingId) {
        RecordingsStore.recordingCreated.next();
        onClose();
        navigate(AppRoutes.mySessionPath(response.proto.recordingId));
      }
    } catch (e) {
      ErrorUtils.fromFetch(e);
    } finally {
      store.creating = false;
    }
  };

  const handleClose = () => {
    store.reset();
    onClose();
  };

  const handleRemoveVideo = () => {
    store.reset();
  };

  const {getRootProps, getInputProps, isDragActive} = useDropzone({
    onDrop,
    accept: {'video/*': ['.mp4', '.mov', '.avi', '.mkv', '.webm', '.m4v']},
    maxFiles: 1,
    disabled: store.isUploading || store.isUploaded,
  });

  return (
    <Dialog
      open={open}
      onClose={(_, reason) => {
        if (reason !== 'backdropClick') handleClose();
      }}
      maxWidth="md"
      fullWidth
      slotProps={{
        paper: {
          style: {
            borderRadius: D.radius(2),
            maxHeight: 'calc(100vh - 40px)',
          }
        }
      }}
    >
      {/* Header */}
      <Frame
        row
        alignItems="center"
        justifyContent="space-between"
        padding={3}
        style={{paddingBottom: D.unit(2)}}
      >
        <Typography variant="h4">
          Upload Session
        </Typography>
        <IconButton onClick={handleClose}>
          <CloseIcon/>
        </IconButton>
      </Frame>

      <DialogContent style={{paddingTop: 0}}>
        <Frame direction="column" gap={3}>
          {/* Video Upload Area */}
          <Frame direction="column" gap={1}>
            <Typography variant="caption" fontWeight={600} style={{letterSpacing: '0.5px'}}>
              VIDEO FILE
            </Typography>

            {!store.isUploaded && !store.isUploading ? (
              <Frame
                {...getRootProps()}
                clickable
                radius={1.5}
                padding={4}
                center
                style={{
                  border: `2px dashed ${isDragActive ? theme.palette.primary.main : theme.palette.divider}`,
                  backgroundColor: isDragActive ? theme.palette.action.hover : theme.palette.background.paper,
                  transition: 'all 0.2s',
                  minHeight: 150,
                }}
              >
                <input {...getInputProps()} />
                <Frame direction="column" center gap={1}>
                  <VideoFileIcon style={{fontSize: 48, color: theme.palette.text.secondary}}/>
                  <Typography variant="body1" color="text.secondary">
                    {isDragActive ? 'Drop the video here' : 'Drag & drop a video file, or click to select'}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Supported formats: MP4, MOV, AVI, MKV, WebM
                  </Typography>
                </Frame>
              </Frame>
            ) : (
              <Frame
                radius={1.5}
                padding={2}
                style={{
                  border: `1px solid ${theme.palette.divider}`,
                  backgroundColor: theme.palette.background.paper,
                }}
              >
                <Frame row alignItems="center" gap={2}>
                  {/* Video Preview */}
                  {store.videoPreviewUrl && (
                    <Frame
                      radius={1}
                      clip
                      style={{
                        width: 160,
                        flexShrink: 0,
                        border: `1px solid ${theme.palette.divider}`,
                      }}
                    >
                      <video
                        ref={videoRef}
                        src={store.videoPreviewUrl}
                        onLoadedMetadata={handleVideoLoad}
                        style={{
                          width: '100%',
                          aspectRatio: '16/9',
                          objectFit: 'cover',
                          display: 'block',
                        }}
                        muted
                      />
                    </Frame>
                  )}

                  {/* Upload Status */}
                  <Frame direction="column" gap={1} grow>
                    <Typography variant="body1" fontWeight={500}>
                      {uploadStore.file?.name || 'Video file'}
                    </Typography>

                    {store.isUploading && (
                      <Frame direction="column" gap={0.5}>
                        <LinearProgress
                          variant={uploadStore.progress !== undefined ? 'determinate' : 'indeterminate'}
                          value={uploadStore.progress !== undefined ? uploadStore.progress * 100 : undefined}
                          color="primary"
                        />
                        <Typography variant="caption" color="text.secondary">
                          {uploadStore.progress !== undefined
                            ? `Uploading... ${Math.round(uploadStore.progress * 100)}%`
                            : 'Preparing upload...'}
                        </Typography>
                      </Frame>
                    )}

                    {store.isUploaded && (
                      <Typography variant="caption" color="success.main">
                        Upload complete
                      </Typography>
                    )}

                    {uploadStore.error && (
                      <Typography variant="caption" color="error">
                        {uploadStore.error}
                      </Typography>
                    )}
                  </Frame>

                  {/* Remove button */}
                  {store.isUploaded && (
                    <IconButton onClick={handleRemoveVideo} color="error" size="small">
                      <DeleteIcon/>
                    </IconButton>
                  )}
                </Frame>
              </Frame>
            )}
          </Frame>

          {/* Session Metadata Form */}
          <SessionMetaForm
            data={store.formData}
            onChange={(data) => store.setFormData(data)}
            disabled={store.creating}
          />
        </Frame>
      </DialogContent>

      <DialogActions style={{padding: D.unit(3), paddingTop: D.unit(2)}}>
        <Button onClick={handleClose} disabled={store.creating}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleCreate}
          disabled={!store.canCreate || store.creating}
        >
          {store.creating ? 'Creating...' : 'Create Session'}
        </Button>
      </DialogActions>
    </Dialog>
  );
});
